// cspell:ignore Sommel
// src/services/enhancementAnalyticsService.js
/**
 * SommelAI Enhancement Analytics Service
 *
 * Tracks performance metrics, success rates, and provides insights
 * into the enhancement pipeline's effectiveness across different
 * beverage categories and data sources.
 */

/**
 * Configuration for automatic cleanup
 */
const CLEANUP_CONFIG = {
  maxSessions: 10000, // Maximum number of sessions to keep in memory
  maxAge: 7 * 24 * 60 * 60 * 1000, // 7 days in milliseconds
  cleanupInterval: 60 * 60 * 1000, // Cleanup every hour
};

/**
 * Analytics data store - in production this would be a proper database
 */
let analyticsStore = {
  sessions: new Map(),
  global: {
    totalEnhancements: 0,
    successfulEnhancements: 0,
    failedEnhancements: 0,
    averageConfidence: 0,
    averageProcessingTime: 0,
    categoryStats: {},
    sourceStats: {
      ai: { success: 0, failure: 0, totalTime: 0 },
      external: { success: 0, failure: 0, totalTime: 0 },
      fallback: { success: 0, failure: 0, totalTime: 0 },
    },
    dailyStats: new Map(),
    errorPatterns: new Map(),
  },
};

/**
 * Track enhancement request
 */
export function trackEnhancementStart(beverage, category, sessionId = null) {
  const id = sessionId || generateSessionId();
  const session = {
    id,
    beverage: {
      name: beverage.name,
      category: category,
    },
    startTime: Date.now(),
    events: [],
    sources: {
      ai: { status: "pending", startTime: Date.now() },
      external: { status: "pending", startTime: Date.now() },
      fallback: { status: "not_used" },
    },
  };

  analyticsStore.sessions.set(id, session);
  console.log(
    `📊 Analytics: Started tracking session ${id} for ${beverage.name}`
  );

  return id;
}

/**
 * Track enhancement completion
 */
export function trackEnhancementComplete(sessionId, result) {
  const session = analyticsStore.sessions.get(sessionId);
  if (!session) return;

  const endTime = Date.now();
  const totalTime = endTime - session.startTime;

  session.endTime = endTime;
  session.totalTime = totalTime;
  session.success = true;
  session.confidence = result.masterEnhancement?.confidence?.overall || 0;
  session.sources.ai.confidence = result.masterEnhancement?.confidence?.ai || 0;
  session.sources.external.confidence =
    result.masterEnhancement?.confidence?.external || 0;

  // Update global stats
  updateGlobalStats(session, result);
  updateCategoryStats(session.beverage.category, session);
  updateDailyStats();

  console.log(`📊 Analytics: Completed session ${sessionId} in ${totalTime}ms`);

  return generateSessionSummary(session);
}

/**
 * Track enhancement failure
 */
export function trackEnhancementFailure(sessionId, error) {
  const session = analyticsStore.sessions.get(sessionId);
  if (!session) return;

  const endTime = Date.now();
  session.endTime = endTime;
  session.totalTime = endTime - session.startTime;
  session.success = false;
  session.error = error.message || error;

  // Update global stats
  analyticsStore.global.failedEnhancements++;
  updateErrorPatterns(error);
  updateDailyStats();

  console.log(
    `📊 Analytics: Failed session ${sessionId}: ${error.message || error}`
  );
}

/**
 * Track individual source performance
 */
export function trackSourceComplete(sessionId, source, success, data = null) {
  const session = analyticsStore.sessions.get(sessionId);
  if (!session) return;

  const endTime = Date.now();
  session.sources[source].endTime = endTime;
  session.sources[source].processingTime =
    endTime - session.sources[source].startTime;
  session.sources[source].status = success ? "success" : "failed";

  if (data) {
    session.sources[source].confidence = data.confidence || 0;
    session.sources[source].sources = data.sources || [];
  }

  // Update source-specific stats
  const sourceStats = analyticsStore.global.sourceStats[source];
  if (success) {
    sourceStats.success++;
  } else {
    sourceStats.failure++;
  }
  sourceStats.totalTime += session.sources[source].processingTime;

  console.log(
    `📊 Analytics: ${source} completed for session ${sessionId} (${
      success ? "success" : "failed"
    })`
  );
}

/**
 * Get current analytics dashboard data
 */
export function getAnalyticsDashboard() {
  const global = analyticsStore.global;
  const activeSessions = Array.from(analyticsStore.sessions.values()).filter(
    (session) => !session.endTime
  );

  const recentSessions = Array.from(analyticsStore.sessions.values())
    .filter((session) => session.endTime)
    .sort((a, b) => b.endTime - a.endTime)
    .slice(0, 10);

  return {
    overview: {
      totalEnhancements: global.totalEnhancements,
      successRate:
        global.totalEnhancements > 0
          ? (
              (global.successfulEnhancements / global.totalEnhancements) *
              100
            ).toFixed(1)
          : 0,
      averageConfidence: global.averageConfidence.toFixed(1),
      averageProcessingTime: Math.round(global.averageProcessingTime),
      activeSessions: activeSessions.length,
    },
    sourcePerformance: {
      ai: calculateSourceMetrics("ai"),
      external: calculateSourceMetrics("external"),
      fallback: calculateSourceMetrics("fallback"),
    },
    categoryStats: global.categoryStats,
    recentSessions: recentSessions.map(formatSessionSummary),
    dailyTrends: Array.from(global.dailyStats.entries())
      .slice(-7)
      .map(([date, stats]) => ({ date, ...stats })),
    topErrors: Array.from(global.errorPatterns.entries())
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 5)
      .map(([error, data]) => ({
        error,
        count: data.count,
        lastSeen: data.lastSeen,
      })),
  };
}

/**
 * Get detailed session analytics
 */
export function getSessionAnalytics(sessionId) {
  const session = analyticsStore.sessions.get(sessionId);
  if (!session) return null;

  return {
    ...session,
    timeline: generateSessionTimeline(session),
    performance: {
      totalTime: session.totalTime,
      sourceBreakdown: Object.entries(session.sources).map(
        ([source, data]) => ({
          source,
          time: data.processingTime || 0,
          status: data.status,
          confidence: data.confidence || 0,
        })
      ),
    },
  };
}

/**
 * Get performance recommendations
 */
export function getPerformanceRecommendations() {
  const global = analyticsStore.global;
  const recommendations = [];

  // AI performance recommendations
  const aiStats = global.sourceStats.ai;
  const aiSuccessRate = aiStats.success / (aiStats.success + aiStats.failure);
  if (aiSuccessRate < 0.8) {
    recommendations.push({
      type: "warning",
      area: "AI Services",
      message: `AI success rate is ${(aiSuccessRate * 100).toFixed(
        1
      )}%. Consider checking API keys and rate limits.`,
      priority: "high",
    });
  }

  // External API recommendations
  const extStats = global.sourceStats.external;
  const extSuccessRate =
    extStats.success / (extStats.success + extStats.failure);
  if (extSuccessRate < 0.7) {
    recommendations.push({
      type: "warning",
      area: "External APIs",
      message: `External API success rate is ${(extSuccessRate * 100).toFixed(
        1
      )}%. Review API configurations.`,
      priority: "medium",
    });
  }

  // Performance recommendations
  if (global.averageProcessingTime > 5000) {
    recommendations.push({
      type: "performance",
      area: "Processing Time",
      message: `Average processing time is ${Math.round(
        global.averageProcessingTime
      )}ms. Consider optimizing parallel requests.`,
      priority: "medium",
    });
  }

  // Confidence recommendations
  if (global.averageConfidence < 0.6) {
    recommendations.push({
      type: "info",
      area: "Data Quality",
      message: `Average confidence is ${(
        global.averageConfidence * 100
      ).toFixed(1)}%. Fallback service is providing good coverage.`,
      priority: "low",
    });
  }

  return recommendations;
}

/**
 * Export analytics data for external analysis
 */
export function exportAnalyticsData(format = "json") {
  const data = {
    exportTime: new Date().toISOString(),
    global: analyticsStore.global,
    sessions: Array.from(analyticsStore.sessions.entries()).map(
      ([id, session]) => ({
        id,
        ...session,
      })
    ),
  };

  switch (format) {
    case "csv":
      return convertToCSV(data);
    case "json":
    default:
      return JSON.stringify(data, null, 2);
  }
}

/**
 * Clear analytics data (for testing/reset)
 */
export function clearAnalytics() {
  analyticsStore = {
    sessions: new Map(),
    global: {
      totalEnhancements: 0,
      successfulEnhancements: 0,
      failedEnhancements: 0,
      averageConfidence: 0,
      averageProcessingTime: 0,
      categoryStats: {},
      sourceStats: {
        ai: { success: 0, failure: 0, totalTime: 0 },
        external: { success: 0, failure: 0, totalTime: 0 },
        fallback: { success: 0, failure: 0, totalTime: 0 },
      },
      dailyStats: new Map(),
      errorPatterns: new Map(),
    },
  };
  console.log("📊 Analytics: Data cleared");
}

// Helper functions

function generateSessionId() {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

function updateGlobalStats(session, result) {
  const global = analyticsStore.global;

  global.totalEnhancements++;
  global.successfulEnhancements++;

  // Update averages
  const currentTotal = global.totalEnhancements;
  global.averageConfidence =
    (global.averageConfidence * (currentTotal - 1) + session.confidence) /
    currentTotal;
  global.averageProcessingTime =
    (global.averageProcessingTime * (currentTotal - 1) + session.totalTime) /
    currentTotal;
}

function updateCategoryStats(category, session) {
  if (!analyticsStore.global.categoryStats[category]) {
    analyticsStore.global.categoryStats[category] = {
      total: 0,
      successful: 0,
      totalTime: 0,
      totalConfidence: 0,
    };
  }

  const stats = analyticsStore.global.categoryStats[category];
  stats.total++;
  stats.successful++;
  stats.totalTime += session.totalTime;
  stats.totalConfidence += session.confidence;
}

function updateDailyStats() {
  const today = new Date().toISOString().split("T")[0];

  if (!analyticsStore.global.dailyStats.has(today)) {
    analyticsStore.global.dailyStats.set(today, {
      enhancements: 0,
      successes: 0,
      failures: 0,
    });
  }

  const todayStats = analyticsStore.global.dailyStats.get(today);
  todayStats.enhancements++;
  todayStats.successes++;
}

function updateErrorPatterns(error) {
  const errorKey = error.message || error.toString();

  if (!analyticsStore.global.errorPatterns.has(errorKey)) {
    analyticsStore.global.errorPatterns.set(errorKey, {
      count: 0,
      firstSeen: new Date().toISOString(),
      lastSeen: new Date().toISOString(),
    });
  }

  const errorData = analyticsStore.global.errorPatterns.get(errorKey);
  errorData.count++;
  errorData.lastSeen = new Date().toISOString();
}

function calculateSourceMetrics(source) {
  const stats = analyticsStore.global.sourceStats[source];
  const total = stats.success + stats.failure;

  return {
    successRate: total > 0 ? ((stats.success / total) * 100).toFixed(1) : 0,
    averageTime: total > 0 ? Math.round(stats.totalTime / total) : 0,
    totalRequests: total,
  };
}

function formatSessionSummary(session) {
  return {
    id: session.id,
    beverage: session.beverage.name,
    category: session.beverage.category,
    success: session.success,
    confidence: session.confidence
      ? `${(session.confidence * 100).toFixed(1)}%`
      : "N/A",
    processingTime: `${session.totalTime}ms`,
    timestamp: new Date(session.endTime).toISOString(),
  };
}

function generateSessionSummary(session) {
  return {
    sessionId: session.id,
    beverage: session.beverage,
    performance: {
      totalTime: session.totalTime,
      confidence: session.confidence,
      sources: Object.entries(session.sources).reduce((acc, [source, data]) => {
        acc[source] = {
          status: data.status,
          time: data.processingTime,
          confidence: data.confidence,
        };
        return acc;
      }, {}),
    },
  };
}

function generateSessionTimeline(session) {
  const timeline = [
    {
      time: 0,
      event: "Enhancement Started",
      details: `${session.beverage.name} (${session.beverage.category})`,
    },
  ];

  Object.entries(session.sources).forEach(([source, data]) => {
    if (data.endTime) {
      timeline.push({
        time: data.processingTime,
        event: `${source} ${data.status}`,
        details: `Confidence: ${
          data.confidence ? (data.confidence * 100).toFixed(1) + "%" : "N/A"
        }`,
      });
    }
  });

  timeline.push({
    time: session.totalTime,
    event: "Enhancement Complete",
    details: `Overall confidence: ${
      session.confidence ? (session.confidence * 100).toFixed(1) + "%" : "N/A"
    }`,
  });

  return timeline.sort((a, b) => a.time - b.time);
}

function convertToCSV(data) {
  const sessions = data.sessions;
  const headers = [
    "Session ID",
    "Beverage Name",
    "Category",
    "Success",
    "Total Time",
    "Confidence",
    "AI Status",
    "External Status",
    "Timestamp",
  ];

  const rows = sessions.map((session) => [
    session.id,
    session.beverage.name,
    session.beverage.category,
    session.success ? "Yes" : "No",
    session.totalTime || 0,
    session.confidence || 0,
    session.sources.ai.status,
    session.sources.external.status,
    session.endTime ? new Date(session.endTime).toISOString() : "In Progress",
  ]);

  return [headers, ...rows].map((row) => row.join(",")).join("\n");
}

/**
 * Automatic cleanup of old sessions to prevent memory leaks
 */
function cleanupOldSessions() {
  const now = Date.now();
  const sessions = analyticsStore.sessions;

  // Remove sessions older than maxAge
  for (const [id, session] of sessions.entries()) {
    if (now - session.startTime > CLEANUP_CONFIG.maxAge) {
      sessions.delete(id);
    }
  }

  // If still too many sessions, remove oldest ones
  if (sessions.size > CLEANUP_CONFIG.maxSessions) {
    const sortedSessions = Array.from(sessions.entries()).sort(
      (a, b) => a[1].startTime - b[1].startTime
    );

    const toRemove = sessions.size - CLEANUP_CONFIG.maxSessions;
    for (let i = 0; i < toRemove; i++) {
      sessions.delete(sortedSessions[i][0]);
    }
  }

  console.log(`🧹 Cleaned up old sessions. Current count: ${sessions.size}`);
}

/**
 * Start automatic cleanup interval
 */
let cleanupInterval = setInterval(
  cleanupOldSessions,
  CLEANUP_CONFIG.cleanupInterval
);

/**
 * Stop automatic cleanup (for testing or shutdown)
 */
export function stopCleanup() {
  if (cleanupInterval) {
    clearInterval(cleanupInterval);
    cleanupInterval = null;
  }
}

const analyticsService = {
  trackEnhancementStart,
  trackEnhancementComplete,
  trackEnhancementFailure,
  trackSourceComplete,
  getAnalyticsDashboard,
  getSessionAnalytics,
  getPerformanceRecommendations,
  exportAnalyticsData,
  clearAnalytics,
};

export default analyticsService;
